import {
  InvalidSessionErrorsCodes,
  errorType,
  ErrorType,
} from "../types/error.definition";
import { Error, ErrorField } from "../types/error.definition";

/**
 * A rejection body is usable as long as it carries a code and *something* to
 * say. The API omits `message` on a per-field validation failure
 * (`ApiError.message` is @JsonInclude(NON_EMPTY)), e.g.
 * `{"code":"VALIDATION_FAILED","fields":[…]}` — requiring `message` there sent
 * the whole body, field list included, down the "UNHANDLED_ERROR" path.
 */
export const isValidErrorFormat = (error: Error) =>
  !!error.code && (!!error.message || getApiFieldErrors(error).length > 0);

export const errorCategoryMapper = (error: Error) => {
  const tmpError: Error = { ...error };
  let category: ErrorType = errorType.RETRIABLE_UNEXPECTED_ERROR;

  if (InvalidSessionErrorsCodes.includes(error.code)) {
    category = errorType.NO_RETRIABLE_UNEXPECTED_ERROR;
  } else {
    category = errorType.RETRIABLE_UNEXPECTED_ERROR;
  }
  return { ...tmpError, category };
};

/**
 * MED-1458: a 400 from medecine360-api carries a per-field list
 * (`ApiError.fields` → `BffErrorResponse.fields`), which the axios interceptor
 * copies onto the rejection. Pull it off whatever the caller was rejected with,
 * so a screen can show each message against its own field instead of the
 * generic "invalid payload" modal.
 */
export const getApiFieldErrors = (error: unknown): ErrorField[] => {
  const fields = (error as { fields?: unknown })?.fields;
  if (!Array.isArray(fields)) return [];
  return (
    fields
      .filter((f): f is Partial<ErrorField> => !!f && typeof f === "object")
      .map(f => ({
        name: typeof f.name === "string" ? f.name.trim() : "",
        message: typeof f.message === "string" ? f.message.trim() : "",
      }))
      // Only an entry with neither a name nor a message says nothing at all. An
      // entry the server did not name is still kept, so no message is ever
      // dropped for want of a field name.
      .filter(f => !!f.name || !!f.message)
  );
};

/**
 * Which of a form's own fields the API's field names and error codes belong to.
 * The API names fields in camelCase (`promotionCode`); a form's field names are
 * its own, so the mapping is always explicit — never assumed to match.
 */
export interface ApiFormErrorMapping<F extends string> {
  /** API field name (as sent, e.g. `promotionCode` or `location.city`) → form field. */
  fields?: Record<string, F>;
  /** API error code → the form field its top-level message belongs on. */
  codes?: Record<string, F>;
}

export interface ApiFieldMessage<F extends string> {
  /** This form's field name. */
  field: F;
  /** What the API called it. */
  apiName: string;
  /** The API's own message, already translated by the BFF. May be empty. */
  message: string;
}

export interface ResolvedApiFormError<F extends string> {
  /** One entry per named field this form has an input for, in the order received. */
  fieldErrors: ApiFieldMessage<F>[];
  /** Field entries this form has no input for. Never dropped — the caller must show them. */
  unmappedFields: ErrorField[];
  /** The rejection's own message, when no field list and no code mapping consumed it. */
  message: string | null;
  /**
   * False only when the rejection carried neither a field list nor a message —
   * the one case where the generic error modal is still the right answer.
   */
  handled: boolean;
}

/**
 * Work out what a rejected API call actually tells a form. Pure counterpart of
 * `useApiFormErrors`, which composes these parts into displayable text.
 */
export const resolveApiFormError = <F extends string>(
  error: unknown,
  mapping: ApiFormErrorMapping<F> = {}
): ResolvedApiFormError<F> => {
  const fields = getApiFieldErrors(error);
  const rawCode = (error as { code?: unknown })?.code;
  const code = typeof rawCode === "string" ? rawCode : undefined;
  const rawMessage = (error as { message?: unknown })?.message;
  const message =
    typeof rawMessage === "string" && rawMessage.trim()
      ? rawMessage.trim()
      : null;

  const fieldErrors: ApiFieldMessage<F>[] = [];
  const unmappedFields: ErrorField[] = [];

  for (const field of fields) {
    const target = field.name ? mapping.fields?.[field.name] : undefined;
    if (target) {
      fieldErrors.push({
        field: target,
        apiName: field.name,
        message: field.message,
      });
    } else {
      unmappedFields.push(field);
    }
  }

  // A field list is the most precise thing the server can send, so it wins over
  // the top-level message (which is a generic "validation failed" summary).
  if (fields.length > 0) {
    return { fieldErrors, unmappedFields, message: null, handled: true };
  }

  // No field list, but a code whose message belongs on one input anyway —
  // PROMOTION_REBATE_INVALID explains the rebate field, for instance.
  const codeTarget = code ? mapping.codes?.[code] : undefined;
  if (codeTarget && message) {
    return {
      fieldErrors: [{ field: codeTarget, apiName: code as string, message }],
      unmappedFields,
      message: null,
      handled: true,
    };
  }

  return { fieldErrors, unmappedFields, message, handled: message !== null };
};
